import type { FC } from "react";
import { memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";

import { BoardPolyOptionVirtualized } from "@/components/QuickSearch/components/BoardDropdown/components/BoardPolyOptionVirtualized";
import type { BoardDropdownOption } from "@/components/QuickSearch/components/BoardDropdown/models/boardDropdownOption";
import { RenderMenu } from "@/components/QuickSearch/components/DropdownList/components/RenderMenu";
import type { MenuRendererProps } from "@/components/QuickSearch/components/DropdownList/models/menuRenderProps";
import { usePolyBoardOptions } from "@/components/QuickSearch/hooks/usePolyBoardOptions";
import { getSearchBoardsData } from "@/components/QuickSearch/state/mondaySearchReducer";
import { getThemeTokenValue } from "@/design/themes/Themes";
import { get } from "@/utils/theme-gets";

export type BoardDropdownMenuProps = MenuRendererProps<BoardDropdownOption>;

export const BoardDropdownMenu: FC<BoardDropdownMenuProps> = memo(
  ({ options, selectOption, getValue, hasValue, setValue, maxMenuHeight }) => {
    const theme = useTheme();

    const listPadding = useMemo(
      () => parseFloat(getThemeTokenValue(get(theme, "spacing.s", null))),
      [get(theme, "spacing.s", null)]
    );

    const boardOptionHeight = useMemo(
      () => parseFloat(getThemeTokenValue(get(theme, "size.l", null))),
      [get(theme, "size.l", null)]
    );

    const selectedValue = useMemo(() => {
      return hasValue ? getValue()[0] : null;
    }, [hasValue, getValue]);

    const { isLoading } = useSelector(getSearchBoardsData);

    const isNoResults = !isLoading && options.length === 0;

    const aggregateOnChange = useCallback(
      (item: BoardDropdownOption) => {
        setValue(item);
        selectOption(item);
      },
      [setValue, selectOption]
    );

    const polyBoards = usePolyBoardOptions(
      options,
      selectedValue?.value || null,
      isLoading,
      isNoResults,
      boardOptionHeight,
      aggregateOnChange
    );

    const selectedIndex = useMemo(() => {
      return polyBoards.findIndex((cur) => cur.board && cur.board.value === selectedValue?.value);
    }, [polyBoards, selectedValue?.value]);

    const menuHeight = useMemo(() => {
      const itemsHeight = polyBoards.length * boardOptionHeight + listPadding * 2;
      return Math.min(itemsHeight, maxMenuHeight);
    }, [boardOptionHeight, listPadding, maxMenuHeight, polyBoards.length]);

    return (
      <RenderMenu
        options={polyBoards}
        menuHeight={menuHeight}
        selectedIndex={selectedIndex}
        virtualizedItem={BoardPolyOptionVirtualized}
        data-testId="cpp-board-drop-down-menu"
      />
    );
  }
);
